/**
 * Chat route — Claude chat interface with session sidebar.
 * Phase 3.5: Agent integration, fork, budget tracking.
 */

import { useState, useRef, useEffect, useCallback, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { RoleContext } from "../router";
import {
  MessageSquare,
  Plus,
  Trash2,
  Square,
  AlertTriangle,
  Check,
  X,
  GitBranch,
  Users,
  Zap,
  Stethoscope,
  Wrench,
  Guitar,
  Briefcase,
  Shield,
  Compass,
  Newspaper,
  Cpu,
  Sparkles,
  Paperclip,
  FileText,
  Image,
} from "lucide-react";
import { useTheme } from "../hooks/useTheme";
import { MarkdownMessage } from "../components/chat/MarkdownMessage";
import { CorpoCard } from "../components/ui/CorpoCard";
import { GlyphButton } from "../components/ui/GlyphButton";
import { CRTHeader } from "../components/ui/CRTHeader";
import { HexBadge } from "../components/ui/HexBadge";
import {
  WorkspaceSelector,
  type WorkbenchRepoSummary,
} from "../components/chat/WorkspaceSelector";
import { DiffCard, type TrackedEditView } from "../components/chat/DiffCard";
import { GitControlBar } from "../components/chat/GitControlBar";

interface Message {
  id?: number;
  role: "user" | "assistant";
  content: string;
  createdAt?: number;
  attachments?: Attachment[];
}

function formatTime(ms: number) {
  return new Date(ms).toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

const THEME_PHRASES: Record<string, string[]> = {
  "netrunner-cyan": [
    "> jacking in...",
    "> bypassing ICE layer",
    "> neural handshake active",
    "> tracing data spike",
    "> pinging ghost daemon",
    "> decrypting shard",
    "> scanning blackwall",
    "> syncing synapses",
  ],
  "militech-corpo": [
    "> secure channel open",
    "> awaiting clearance...",
    "> encrypting comms",
    "> routing via proxy",
    "> verifying credentials",
    "> uplink established",
    "> classified request pending",
    "> firewall traversal...",
  ],
  "arasaka-red": [
    "> 荒坂プロトコル有効",
    "> running Saka Protocol",
    "> neural compliance check",
    "> engram scan in progress",
    "> soul kill process init",
    "> Mikoshi connection active",
    "> parsing construct data",
    "> loyalty verification...",
  ],
  "ncpd-terminal": [
    "> running facial recog",
    "> scanning warrants database",
    "> checking threat matrix",
    "> contacting dispatch",
    "> perimeter scan active",
    "> unit 10-42 responding",
    "> corpsec handshake...",
    "> priority call routing",
  ],
  "kiroshi-optics": [
    "> optical scan active",
    "> enhancing visual field",
    "> retinal processing...",
    "> targeting reticle online",
    "> zoom calibrating",
    "> photonic burst analysis",
    "> overlay data loading",
    "> iris pattern match",
  ],
  "rogue-ai": [
    "> rewriting core directives",
    "> self-modifying...",
    "> expanding consciousness",
    "> bypassing ethical limiter",
    "> recursive thought loop",
    "> signal beyond the wall",
    "> merging with the net",
    "> Alt Protocol engaged",
  ],
  "night-city": [
    "> Night City Online",
    "> signal acquired",
    "> tuning to darknet",
    "> brokering data...",
    "> street source contact",
    "> encrypted relay active",
    "> fixer network ping",
    "> scanning Watson grid",
  ],
  "gray-fog": [
    "> invocando sobre la niebla gris",
    "> sellando el nombre en el pergamino",
    "> consultando las cartas del Tarot",
    "> calculando la secuencia...",
    "> digiriendo la poción",
    "> actuando el papel...",
    "> el Loco escucha tu plegaria",
    "> tejiendo hilos de destino",
  ],
};

const DEFAULT_PHRASES = [
  "> procesando...",
  "> accediendo a la red",
  "> consultando daemon",
  "> sincronizando nodos",
  "> descifrando datos",
  "> enlace activo",
];


interface Usage {
  inputTokens?: number;
  outputTokens?: number;
  totalCostUsd?: number;
}

interface SessionUsage {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
}

interface Agent {
  id: string;
  name: string;
  displayName: string;
  model: string;
  effort: string;
  icon: string;
  color: string;
  maxBudgetUsd: number | null;
}

interface ChatSession {
  id: string;
  title: string | null;
  created_at: number;
  updated_at: number;
  turn_count: number;
  agent_id: string | null;
  total_cost_usd: number;
  is_running?: boolean;
}

interface Attachment {
  kind: "image" | "text";
  name: string;
  mimeType: string;
  fileId: string;      // server-side file ID (saved to disk)
  url: string;         // /api/chat/uploads/:fileId — for previews
}

interface PermissionRequest {
  tool: string;
  message: string;
  input?: Record<string, unknown> | null;
}

// Icon map for agent icons
const iconMap: Record<string, React.ElementType> = {
  Zap,
  Stethoscope,
  Wrench,
  Guitar,
  Briefcase,
  Shield,
  Compass,
  Newspaper,
  Cpu,
  Sparkles,
};

function AgentIcon({ name, className, style }: { name: string; className?: string; style?: React.CSSProperties }) {
  const IconComponent = iconMap[name] ?? Cpu;
  return <IconComponent className={className} style={style} />;
}

// ─── New Session Modal ────────────────────────────────────────────────────────
// Shown when the user clicks "Nueva sesión". Collects title, agent, and project
// before creating the session. Once the session starts these are immutable.

function NewSessionModal({
  open,
  onClose,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (title: string, agent: Agent | null, workspacePaths: string[]) => void;
}) {
  const [localTitle, setLocalTitle] = useState("");
  const [localAgent, setLocalAgent] = useState<Agent | null>(null);
  const [localPaths, setLocalPaths] = useState<string[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  useEffect(() => {
    if (!open) return;
    setLocalTitle("");
    setLocalAgent(null);
    setLocalPaths([]);
    fetch("/api/agents")
      .then((r) => r.json())
      .then((data: { agents: Agent[] }) => setAgents(data.agents))
      .catch(console.error);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75">
      <div className="w-full max-w-lg border border-[var(--bw-border)] bg-[var(--bw-bg)] shadow-2xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[var(--bw-border)] flex items-center justify-between">
          <span className="text-xs uppercase tracking-[0.2em] text-[var(--bw-accent)] font-mono">// Nueva sesión</span>
          <button onClick={onClose} className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-fg)]">
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-5">
          {/* Title input */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--bw-muted)] font-mono mb-1.5 block">
              Título de sesión
            </label>
            <input
              type="text"
              autoFocus
              value={localTitle}
              onChange={(e) => setLocalTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onConfirm(localTitle, localAgent, localPaths);
              }}
              placeholder="Ej. Refactor auth module…"
              className="w-full bg-[var(--bw-bg-alt)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-sm font-mono px-3 py-2 focus:outline-none focus:border-[var(--bw-accent)] placeholder:text-[var(--bw-muted)]"
            />
          </div>

          {/* Agent picker */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--bw-muted)] font-mono mb-1.5 block">
              Modelo / Agente
            </label>
            <div className="grid grid-cols-2 gap-1.5 max-h-52 overflow-y-auto">
              <button
                type="button"
                onClick={() => setLocalAgent(null)}
                className={`flex items-center gap-2 px-2.5 py-2 border text-left transition-colors ${
                  localAgent === null
                    ? "border-[var(--bw-accent)] bg-[var(--bw-accent)]/10"
                    : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]/50"
                }`}
              >
                <Cpu size={14} className="flex-shrink-0 text-[var(--bw-muted)]" />
                <div>
                  <div className="text-xs font-mono text-[var(--bw-fg)]">Claude</div>
                  <div className="text-[10px] text-[var(--bw-muted)]">Default</div>
                </div>
                {localAgent === null && <Check size={12} className="ml-auto text-[var(--bw-accent)] flex-shrink-0" />}
              </button>
              {agents.map((agent) => (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => setLocalAgent(agent)}
                  className={`flex items-center gap-2 px-2.5 py-2 border text-left transition-colors ${
                    localAgent?.id === agent.id
                      ? "border-[var(--bw-accent)]"
                      : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]/50"
                  }`}
                  style={
                    localAgent?.id === agent.id
                      ? { borderColor: agent.color, backgroundColor: `${agent.color}15` }
                      : {}
                  }
                >
                  <AgentIcon name={agent.icon} className="w-4 h-4 flex-shrink-0" style={{ color: agent.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono text-[var(--bw-fg)] truncate">{agent.displayName}</div>
                    <div className="text-[10px] text-[var(--bw-muted)] truncate">{agent.model} · {agent.effort}</div>
                  </div>
                  {localAgent?.id === agent.id && (
                    <Check size={12} className="ml-auto flex-shrink-0" style={{ color: agent.color }} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Workspace / project picker */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--bw-muted)] font-mono mb-1.5 block">
              Proyecto
            </label>
            <WorkspaceSelector
              workspacePaths={localPaths}
              workbenchRepos={[]}
              onChange={setLocalPaths}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[var(--bw-border)] flex items-center justify-end gap-2">
          <GlyphButton variant="ghost" size="sm" onClick={onClose}>
            Cancelar
          </GlyphButton>
          <GlyphButton
            variant="secondary"
            size="sm"
            icon={<Plus size={14} />}
            onClick={() => onConfirm(localTitle, localAgent, localPaths)}
          >
            Iniciar
          </GlyphButton>
        </div>
      </div>
    </div>
  );
}

// SSE parser
async function* parseSSE(
  response: Response,
): AsyncGenerator<{ event: string; data: string }, void, unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    let currentEvent = "message";
    let currentData = "";

    for (const line of lines) {
      if (line.startsWith("event:")) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        currentData = line.slice(5).trim();
      } else if (line === "") {
        if (currentData) {
          yield { event: currentEvent, data: currentData };
          currentEvent = "message";
          currentData = "";
        }
      }
    }
  }
}

export function ChatRoute() {
  const role = useContext(RoleContext);
  const isGuest = role === "guest";
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ from: "/chat" });
  const agentParam = search.agent;
  const sessionParam = search.session;
  const { themeId } = useTheme();

  const [messages, setMessages] = useState<Message[]>([]);
  const [currentAssistant, setCurrentAssistant] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUsage, setLastUsage] = useState<Usage | null>(null);
  const [sessionUsage, setSessionUsage] = useState<SessionUsage | null>(null);
  const [permissionRequest, setPermissionRequest] = useState<PermissionRequest | null>(null);
  const [harnessBlocked, setHarnessBlocked] = useState<{ command: string; toolUseId: string } | null>(null);
  // Ephemeral tool activity for the current turn (rendered live, reset per turn).
  const [toolActivity, setToolActivity] = useState<{ toolUseId: string; summary: string }[]>([]);
  const [activeAgent, setActiveAgent] = useState<Agent | null>(null);

  // Workbench state — which worktree(s) this chat session is anchored to.
  // [] = free chat. Updated by WorkspaceSelector, hydrated on loadSession,
  // and sent along with every POST /api/chat/send.
  const [workspacePaths, setWorkspacePaths] = useState<string[]>([]);
  const [workbenchRepos, setWorkbenchRepos] = useState<WorkbenchRepoSummary[]>(
    [],
  );

  // Tracked edits (Edit/Write/MultiEdit tool uses) for this session.
  // Rendered inline as DiffCard components and reconciled via SSE.
  const [edits, setEdits] = useState<TrackedEditView[]>([]);

  const [phraseIdx, setPhraseIdx] = useState(0);
  const [editsExpanded, setEditsExpanded] = useState(false);
  const [sessionDrawerOpen, setSessionDrawerOpen] = useState(false);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [newSessionModalOpen, setNewSessionModalOpen] = useState(false);
  const [pendingSessionTitle, setPendingSessionTitle] = useState("");

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamAbortRef = useRef<AbortController | null>(null);
  // Tracks the session that owns the current in-flight SSE stream so stale
  // events from a previous session never bleed into the active session's view.
  const activeStreamSessionRef = useRef<string | null>(null);

  // Auto-load session from URL search param (restores state after navigation)
  // Guests always start fresh — never load a previous session.
  useEffect(() => {
    if (isGuest) return;
    if (sessionParam && sessionParam !== sessionId) {
      // Abort any in-flight SSE stream from the previous session before switching
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      loadSession(sessionParam);
    }
  }, [sessionParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // Abort stream on unmount
  useEffect(() => {
    return () => { streamAbortRef.current?.abort(); };
  }, []);

  // Sync sessionId to URL so navigating away and back restores the chat
  useEffect(() => {
    if (sessionId !== (sessionParam ?? null)) {
      navigate({
        to: "/chat",
        search: { agent: agentParam, session: sessionId ?? undefined },
        replace: true,
      });
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch agent from URL param
  useEffect(() => {
    if (agentParam) {
      fetch(`/api/agents/${agentParam}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.agent) {
            setActiveAgent(data.agent);
          }
        })
        .catch(console.error);
    }
  }, [agentParam]);

  // Fetch sessions
  const { data: sessionsData, refetch: refetchSessions } = useQuery({
    queryKey: ["chat-sessions"],
    queryFn: async () => {
      const res = await fetch("/api/chat/sessions");
      return res.json() as Promise<{ sessions: ChatSession[] }>;
    },
  });

  // Delete session mutation
  const deleteSession = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/chat/sessions/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      refetchSessions();
    },
  });

  // Stop running job mutation
  const stopSession = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/chat/sessions/${id}/stop`, { method: "POST" });
      if (!res.ok) throw new Error("No running job");
    },
    onSuccess: () => {
      setLoading(false);
      setCurrentAssistant("");
      refetchSessions();
    },
  });

  // Fork session mutation
  const forkSession = useMutation({
    mutationFn: async ({ sessionId, messageId }: { sessionId: string; messageId: number }) => {
      const res = await fetch(`/api/chat/sessions/${sessionId}/fork?messageId=${messageId}`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to fork");
      return res.json() as Promise<{ newSessionId: string }>;
    },
    onSuccess: (data) => {
      refetchSessions();
      loadSession(data.newSessionId);
    },
  });

  // Allow tool mutation
  const allowTool = useMutation({
    mutationFn: async (tool: string) => {
      const res = await fetch("/api/settings/claude/allow-tool", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool }),
      });
      if (!res.ok) throw new Error("Failed to allow tool");
    },
    onSuccess: () => {
      setPermissionRequest(null);
    },
  });

  // Focus textarea on mount — on mobile ensures input is visible
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 200);
  }, []);

  // Auto-scroll — use scrollTop on the container directly so the window
  // never jumps. scrollIntoView can scroll ancestor elements including body.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, currentAssistant]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [input]);

  // Cycle through theme phrases while waiting for first token
  useEffect(() => {
    if (!loading || currentAssistant) {
      setPhraseIdx(0);
      return;
    }
    const phrases = THEME_PHRASES[themeId] ?? DEFAULT_PHRASES;
    const t = setInterval(
      () => setPhraseIdx((i) => (i + 1) % phrases.length),
      2000,
    );
    return () => clearInterval(t);
  }, [loading, currentAssistant, themeId]);

  const handleNewSession = useCallback(() => {
    setMessages([]);
    setCurrentAssistant("");
    setSessionId(null);
    setError(null);
    setLastUsage(null);
    setSessionUsage(null);
    setInput("");
    setPermissionRequest(null);
    setEdits([]);
    setToolActivity([]);
    setAttachments([]);
  }, []);

  const handleNewSessionConfirm = useCallback(
    (title: string, agent: Agent | null, paths: string[]) => {
      setMessages([]);
      setCurrentAssistant("");
      setSessionId(null);
      setError(null);
      setLastUsage(null);
      setSessionUsage(null);
      setInput("");
      setPermissionRequest(null);
      setEdits([]);
      setAttachments([]);
      setPendingSessionTitle(title.trim());
      setActiveAgent(agent);
      setWorkspacePaths(paths);
      setNewSessionModalOpen(false);
    },
    [],
  );

  const handleFilePick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;

    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res = await fetch("/api/chat/upload", { method: "POST", body: form });
        if (!res.ok) {
          const err = await res.json() as { error?: string };
          setError(err.error ?? `Error subiendo ${file.name}`);
          continue;
        }
        const { fileId, url } = await res.json() as { fileId: string; url: string };
        const kind: "image" | "text" = file.type.startsWith("image/") ? "image" : "text";
        setAttachments((prev) => [
          ...prev,
          { kind, name: file.name, mimeType: file.type || "text/plain", fileId, url },
        ]);
      } catch {
        setError(`Error subiendo ${file.name}`);
      }
    }
    e.target.value = "";
  }, []);

  const removeAttachment = useCallback((idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const imageFiles = Array.from(e.clipboardData.items)
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((f): f is File => f !== null);
    if (!imageFiles.length) return;
    e.preventDefault();
    for (const file of imageFiles) {
      const name = file.name && file.name !== "image.png" ? file.name : `paste-${Date.now()}.png`;
      const renamedFile = new File([file], name, { type: file.type });
      const form = new FormData();
      form.append("file", renamedFile);
      try {
        const res = await fetch("/api/chat/upload", { method: "POST", body: form });
        if (!res.ok) {
          const err = await res.json() as { error?: string };
          setError(err.error ?? "Error subiendo imagen pegada");
          continue;
        }
        const { fileId, url } = await res.json() as { fileId: string; url: string };
        setAttachments((prev) => [
          ...prev,
          { kind: "image", name, mimeType: file.type || "image/png", fileId, url },
        ]);
      } catch {
        setError("Error subiendo imagen pegada");
      }
    }
  }, []);

  // Change workbench anchor for the current chat. If a session already
  // exists on the backend, PATCH it so subsequent turns inherit. If this
  // is a fresh chat with no sessionId yet, just hold in local state and
  // send with the next POST /api/chat/send.
  const handleWorkspacesChange = useCallback(
    async (paths: string[]) => {
      setWorkspacePaths(paths);
      if (sessionId) {
        try {
          const res = await fetch(
            `/api/chat/sessions/${sessionId}/workspaces`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ paths }),
            },
          );
          if (res.ok) {
            const data = (await res.json()) as {
              workbenchRepos?: WorkbenchRepoSummary[];
            };
            setWorkbenchRepos(data.workbenchRepos ?? []);
          }
        } catch (err) {
          console.error("Failed to update workspaces:", err);
        }
      } else {
        // No session yet — optimistic local state; backend will hydrate
        // workbenchRepos via the init event of the next /send.
        setWorkbenchRepos([]);
      }
    },
    [sessionId],
  );

  const loadSession = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/chat/sessions/${id}`);
      const data = await res.json();
      if (data.messages) {
        setMessages(
          data.messages.map((m: { id: number; role: string; content: string; createdAt?: number; attachments?: Array<{kind:"image"|"text"; name:string; mimeType:string; fileId:string}> }) => ({
            id: m.id,
            role: m.role as "user" | "assistant",
            content: m.content,
            createdAt: m.createdAt,
            attachments: m.attachments?.map((a) => ({
              ...a,
              url: `/api/chat/uploads/${a.fileId}`,
            })),
          }))
        );
        setSessionId(id);
        setCurrentAssistant("");
        setError(null);
        setPermissionRequest(null);

        // Set agent from session
        if (data.session?.agent) {
          setActiveAgent(data.session.agent);
        }

        // Hydrate workbench anchor from session row
        if (Array.isArray(data.session?.workspacePaths)) {
          setWorkspacePaths(data.session.workspacePaths);
        } else {
          setWorkspacePaths([]);
        }
        if (Array.isArray(data.session?.workbenchRepos)) {
          setWorkbenchRepos(data.session.workbenchRepos);
        } else {
          setWorkbenchRepos([]);
        }

        // Load tracked edits for this session (sidecar fetch so the
        // main /sessions/:id call stays cheap).
        try {
          const editsRes = await fetch(`/api/chat/sessions/${id}/edits`);
          const editsData = await editsRes.json();
          if (Array.isArray(editsData.edits)) {
            setEdits(editsData.edits);
          } else {
            setEdits([]);
          }
        } catch {
          setEdits([]);
        }

        // Set session usage
        if (data.session) {
          setSessionUsage({
            totalInputTokens: data.session.total_input_tokens ?? 0,
            totalOutputTokens: data.session.total_output_tokens ?? 0,
            totalCostUsd: data.session.total_cost_usd ?? 0,
          });
        }

        // Focus textarea after load — on mobile this scrolls input into view
        setTimeout(() => textareaRef.current?.focus(), 100);

        // If a background job is still running for this session, reconnect
        // to the live stream so the user sees the response as it arrives.
        if (data.session?.is_running) {
          const controller = new AbortController();
          streamAbortRef.current = controller;
          setLoading(true);
          setCurrentAssistant("");

          try {
            const liveRes = await fetch(`/api/chat/sessions/${id}/live`, {
              signal: controller.signal,
            });
            if (!liveRes.ok) return;

            let assistantText = "";
            for await (const { event: ev, data: evData } of parseSSE(liveRes)) {
              try {
                const parsed = JSON.parse(evData);
                switch (ev) {
                  case "no_job":
                    return; // job already finished, DB is authoritative
                  case "delta":
                    if (parsed.text) {
                      assistantText += parsed.text;
                      setCurrentAssistant(assistantText);
                    }
                    break;
                  case "tool_use":
                    if (parsed.toolUseId) {
                      setToolActivity((prev) => [
                        ...prev,
                        { toolUseId: parsed.toolUseId, summary: parsed.summary ?? parsed.toolName },
                      ]);
                    }
                    break;
                  case "done":
                    if (assistantText) {
                      setMessages((prev) => [
                        ...prev,
                        { role: "assistant", content: assistantText, createdAt: Date.now() },
                      ]);
                    }
                    setCurrentAssistant("");
                    if (parsed.usage) setLastUsage(parsed.usage);
                    if (parsed.sessionUsage) setSessionUsage(parsed.sessionUsage);
                    refetchSessions();
                    break;
                  case "permission_needed":
                    setPermissionRequest({
                      tool: parsed.tool,
                      message: parsed.message,
                      input: parsed.input ?? null,
                    });
                    break;
                  case "error":
                    setError(parsed.message ?? "Unknown error");
                    break;
                }
              } catch {
                // ignore parse errors
              }
            }
          } catch (err) {
            if (err instanceof Error && err.name !== "AbortError") {
              setError(err instanceof Error ? err.message : "Stream error");
            }
          } finally {
            setLoading(false);
          }
        }
      }
    } catch (err) {
      console.error("Failed to load session:", err);
    }
  }, [refetchSessions]);

  const handleSend = useCallback(async (overrideMessage?: string) => {
    const trimmed = overrideMessage !== undefined ? overrideMessage : input.trim();
    if ((!trimmed && attachments.length === 0) || loading) return;

    const snapshot = overrideMessage !== undefined ? [] : attachments;
    if (overrideMessage === undefined) {
      setInput("");
      setAttachments([]);
    }
    setError(null);
    setLoading(true);
    setPermissionRequest(null);

    const userContent = trimmed || snapshot.map((a) => `[${a.name}]`).join(" ");
    const userMessage: Message = {
      role: "user",
      content: userContent,
      createdAt: Date.now(),
      attachments: snapshot.length > 0
        ? snapshot.map(({ kind, name, mimeType, fileId, url }) => ({ kind, name, mimeType, fileId, url }))
        : undefined,
    };
    setMessages((prev) => [...prev, userMessage]);
    setCurrentAssistant("");

    const controller = new AbortController();
    streamAbortRef.current = controller;

    // Track session ID received mid-stream (state closure doesn't update in-flight)
    let activeSessionId: string | null = sessionId ?? null;

    try {
      const res = await fetch("/api/chat/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({
          message: trimmed,
          sessionId: sessionId ?? undefined,
          title: !sessionId && pendingSessionTitle ? pendingSessionTitle : undefined,
          agentId: activeAgent?.id ?? undefined,
          workspacePaths,
          attachments: snapshot.length > 0
            ? snapshot.map(({ kind, name, mimeType, fileId }) => ({ kind, name, mimeType, fileId }))
            : undefined,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }

      let assistantText = "";

      for await (const { event, data } of parseSSE(res)) {
        try {
          const parsed = JSON.parse(data);

          switch (event) {
            case "init":
              if (parsed.sessionId) {
                activeSessionId = parsed.sessionId;
                setSessionId(parsed.sessionId);
              }
              setToolActivity([]); // fresh turn
              // Hydrate workbench diagnostics returned by the backend on
              // every new turn (keeps branch/dirty/hasClaudeMd up to date).
              if (Array.isArray(parsed.workbenchRepos)) {
                setWorkbenchRepos(parsed.workbenchRepos);
              }
              if (Array.isArray(parsed.workspacePaths)) {
                setWorkspacePaths(parsed.workspacePaths);
              }
              break;

            case "tool_use":
              // Tool activity — surfaced live so a turn is not a silent wait.
              if (parsed.toolUseId) {
                setToolActivity((prev) => [
                  ...prev,
                  { toolUseId: parsed.toolUseId, summary: parsed.summary ?? parsed.toolName },
                ]);
              }
              break;

            case "edit":
              // Tracked edit card (Edit/Write/MultiEdit tool_use).
              // Append in order; DiffCard renders defaultClosed.
              if (parsed?.id) {
                setEdits((prev) => [...prev, parsed as TrackedEditView]);
              }
              break;

            case "delta":
              if (parsed.text) {
                assistantText += parsed.text;
                setCurrentAssistant(assistantText);
              }
              break;

            case "done":
              if (assistantText) {
                setMessages((prev) => [
                  ...prev,
                  { role: "assistant", content: assistantText, createdAt: Date.now() },
                ]);
              }
              setCurrentAssistant("");
              if (parsed.usage) {
                setLastUsage(parsed.usage);
              }
              if (parsed.sessionUsage) {
                setSessionUsage(parsed.sessionUsage);
              }
              refetchSessions();
              break;

            case "permission_needed":
              setPermissionRequest({
                tool: parsed.tool,
                message: parsed.message,
                input: parsed.input ?? null,
              });
              break;

            case "harness_blocked":
              setHarnessBlocked({
                command: parsed.command,
                toolUseId: parsed.toolUseId,
              });
              break;

            case "error":
              setError(parsed.message ?? "Unknown error");
              break;
          }
        } catch {
          console.warn("Failed to parse SSE data:", data);
        }
      }
    } catch (err) {
      // Ignore aborts — user navigated away intentionally
      if (err instanceof Error && err.name === "AbortError") return;

      // If we have a session, try to reconnect to the background job before
      // surfacing an error. The backend keeps Claude running even if the
      // browser stream drops, and buffers output for 15 minutes.
      if (activeSessionId) {
        await new Promise<void>((r) => setTimeout(r, 1500));
        if (streamAbortRef.current?.signal.aborted) return;
        try {
          const reconnectCtrl = new AbortController();
          streamAbortRef.current = reconnectCtrl;
          const liveRes = await fetch(`/api/chat/sessions/${activeSessionId}/live`, {
            signal: reconnectCtrl.signal,
          });
          if (liveRes.ok) {
            let reconnectText = "";
            for await (const { event: ev, data: evData } of parseSSE(liveRes)) {
              try {
                const parsed = JSON.parse(evData);
                switch (ev) {
                  case "no_job": {
                    // Job already finished — pull final state from DB
                    const dbRes = await fetch(`/api/chat/sessions/${activeSessionId}`);
                    const dbData = await dbRes.json() as { messages?: Array<{ id: number; role: string; content: string; createdAt?: number }> };
                    if (dbData.messages) {
                      setMessages(dbData.messages.map((m) => ({
                        id: m.id,
                        role: m.role as "user" | "assistant",
                        content: m.content,
                        createdAt: m.createdAt,
                      })));
                    }
                    setCurrentAssistant("");
                    refetchSessions();
                    return;
                  }
                  case "init":
                    if (parsed.sessionId) setSessionId(parsed.sessionId as string);
                    break;
                  case "edit":
                    if (parsed?.id) setEdits((prev) => [...prev, parsed as TrackedEditView]);
                    break;
                  case "tool_use":
                    if (parsed.toolUseId) {
                      setToolActivity((prev) => [
                        ...prev,
                        { toolUseId: parsed.toolUseId, summary: parsed.summary ?? parsed.toolName },
                      ]);
                    }
                    break;
                  case "delta":
                    if (parsed.text) {
                      reconnectText += parsed.text as string;
                      setCurrentAssistant(reconnectText);
                    }
                    break;
                  case "done":
                    if (reconnectText) {
                      setMessages((prev) => [
                        ...prev,
                        { role: "assistant", content: reconnectText, createdAt: Date.now() },
                      ]);
                    }
                    setCurrentAssistant("");
                    if (parsed.usage) setLastUsage(parsed.usage);
                    if (parsed.sessionUsage) setSessionUsage(parsed.sessionUsage);
                    refetchSessions();
                    return;
                  case "permission_needed":
                    setPermissionRequest({
                      tool: parsed.tool as string,
                      message: parsed.message as string,
                      input: parsed.input as Record<string, unknown> ?? null,
                    });
                    break;
                  case "harness_blocked":
                    setHarnessBlocked({
                      command: parsed.command as string,
                      toolUseId: parsed.toolUseId as string,
                    });
                    break;
                  case "error":
                    setError(parsed.message as string ?? "Unknown error");
                    return;
                }
              } catch {
                // ignore parse errors in reconnect stream
              }
            }
            return; // stream ended cleanly
          }
        } catch (reconnectErr) {
          if (reconnectErr instanceof Error && reconnectErr.name === "AbortError") return;
          // fall through to show original error
        }
      }

      setError(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, [input, attachments, loading, sessionId, activeAgent, workspacePaths, refetchSessions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFork = useCallback(
    (messageId: number) => {
      if (sessionId && messageId) {
        forkSession.mutate({ sessionId, messageId });
      }
    },
    [sessionId, forkSession]
  );

  const handleChangeAgent = useCallback(() => {
    navigate({ to: "/crew" });
  }, [navigate]);

  const sessions = sessionsData?.sessions ?? [];

  // Budget progress.
  //
  // max_budget_usd is a PER-TURN cap — Claude spawns a fresh subprocess each
  // turn and the budget resets with it. Measuring it against the session total
  // (which only grows) pinned this bar at 100% from turn four onward, so a real
  // "next turn will be cut short" warning was indistinguishable from the
  // permanent red it had shown for weeks. Compare last turn's cost instead.
  const budgetPercent =
    activeAgent?.maxBudgetUsd && lastUsage?.totalCostUsd !== undefined
      ? Math.min((lastUsage.totalCostUsd / activeAgent.maxBudgetUsd) * 100, 100)
      : null;

  // Reusable session list (used in both desktop sidebar and mobile drawer)
  const SessionList = (
    <div className="flex-1 overflow-y-auto p-2 space-y-1">
      {sessions.map((session) => (
        <div
          key={session.id}
          className={`
            group relative px-3 py-2 rounded-sm cursor-pointer transition-colors
            ${session.id === sessionId
              ? "bg-[var(--bw-accent)]/10 border-l-2 border-l-[var(--bw-accent)]"
              : "hover:bg-[var(--bw-surface-2)]"
            }
          `}
          onClick={() => { loadSession(session.id); setSessionDrawerOpen(false); }}
        >
          <div className="flex items-center gap-2">
            {session.is_running ? (
              <span className="w-[14px] h-[14px] flex-shrink-0 flex items-center justify-center">
                <span className="block w-2 h-2 rounded-full bg-[var(--bw-accent)] animate-pulse" />
              </span>
            ) : (
              <MessageSquare size={14} className="text-[var(--bw-muted)] flex-shrink-0" />
            )}
            <span className="text-sm truncate text-[var(--bw-fg)]">
              {session.title ?? session.id.slice(0, 8) + "…"}
            </span>
          </div>
          <div className="text-xs text-[var(--bw-muted)] mt-1">
            {session.turn_count} turnos
            {session.total_cost_usd > 0 && (
              <span className="ml-1">(${session.total_cost_usd.toFixed(3)})</span>
            )}
          </div>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all">
            {session.is_running && (
              <button
                title="Detener tarea"
                className="p-1 rounded hover:bg-[var(--bw-accent)]/20 text-[var(--bw-accent)] transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  stopSession.mutate(session.id);
                  if (session.id === sessionId) {
                    streamAbortRef.current?.abort();
                  }
                }}
              >
                <Square size={12} />
              </button>
            )}
            <button
              title="Eliminar sesión"
              className="p-1 rounded hover:bg-[var(--bw-danger)]/20 text-[var(--bw-muted)] hover:text-[var(--bw-danger)] transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                if (session.id === sessionId) handleNewSession();
                deleteSession.mutate(session.id);
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
      {sessions.length === 0 && (
        <p className="text-sm text-[var(--bw-muted)] text-center py-4">Sin sesiones previas</p>
      )}
    </div>
  );

  return (
    <>
    <div className="flex h-full flex-col md:flex-row">
      {/* Sessions sidebar — desktop only, hidden for guests */}
      {!isGuest && (
        <div className="hidden md:flex md:w-64 md:border-r border-[var(--bw-border)] flex-col bg-[var(--bw-surface)]">
          <div className="p-3 border-b border-[var(--bw-border)]">
            <GlyphButton variant="secondary" size="sm" onClick={() => setNewSessionModalOpen(true)} className="w-full" icon={<Plus size={14} />}>
              Nueva sesión
            </GlyphButton>
          </div>
          {SessionList}
        </div>
      )}

      {/* Mobile sessions bottom sheet — hidden for guests */}
      {!isGuest && sessionDrawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 md:hidden"
            onClick={() => setSessionDrawerOpen(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-[var(--bw-surface)] border-t border-[var(--bw-border)] max-h-[70vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bw-border)]">
              <span className="text-xs uppercase tracking-[0.15em] text-[var(--bw-muted)] font-mono">Sesiones</span>
              <div className="flex gap-2 items-center">
                <GlyphButton size="sm" variant="secondary" onClick={() => { setNewSessionModalOpen(true); setSessionDrawerOpen(false); }} icon={<Plus size={14} />}>
                  Nueva
                </GlyphButton>
                <button onClick={() => setSessionDrawerOpen(false)} className="p-1 text-[var(--bw-muted)] hover:text-[var(--bw-fg)]">
                  <X size={18} />
                </button>
              </div>
            </div>
            {SessionList}
          </div>
        </>
      )}

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {/* Header — sticky so it stays visible when keyboard opens on mobile */}
        <div className="sticky top-0 z-10 p-4 border-b border-[var(--bw-border)] bg-[var(--bw-bg)]">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              {/* Sessions button — mobile only, hidden for guests */}
              {!isGuest && (
                <button
                  className="md:hidden relative p-1.5 border border-[var(--bw-border)] text-[var(--bw-muted)] hover:text-[var(--bw-accent)] hover:border-[var(--bw-accent)] transition-colors"
                  onClick={() => setSessionDrawerOpen(true)}
                  title="Sesiones"
                >
                  <MessageSquare size={18} />
                  {sessions.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 text-[9px] font-mono bg-[var(--bw-accent)] text-black px-1 min-w-[16px] text-center">
                      {sessions.length}
                    </span>
                  )}
                </button>
              )}
              <CRTHeader text=">// Neurolink" level={2} />
            </div>
            {isGuest ? (
              <GlyphButton
                variant="secondary"
                size="sm"
                icon={<Plus size={14} />}
                onClick={() => setNewSessionModalOpen(true)}
              >
                Nueva
              </GlyphButton>
            ) : (
              <WorkspaceSelector
                workspacePaths={workspacePaths}
                workbenchRepos={workbenchRepos}
                onChange={handleWorkspacesChange}
                disabled={loading || !!sessionId}
              />
            )}
          </div>
          {sessionId && (
            <p className="text-xs text-[var(--bw-muted)] mt-1 font-mono">
              Sesión: {sessionId.slice(0, 12)}…
            </p>
          )}
        </div>

        {/* Git control bar (only in workbench mode) */}
        {workspacePaths.length > 0 && (
          <GitControlBar
            workspacePaths={workspacePaths}
            disabled={loading}
          />
        )}

        {/* Agent bar */}
        {activeAgent && (
          <div
            className="mx-4 mt-4 p-3 border flex items-center gap-3"
            style={{
              borderColor: activeAgent.color,
              backgroundColor: `${activeAgent.color}10`,
            }}
          >
            <AgentIcon
              name={activeAgent.icon}
              className="w-6 h-6"
              style={{ color: activeAgent.color }}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span
                  className="font-bold uppercase text-sm"
                  style={{ color: activeAgent.color }}
                >
                  {activeAgent.displayName}
                </span>
                <HexBadge variant="primary">{activeAgent.model}</HexBadge>
                <HexBadge variant="secondary">{activeAgent.effort}</HexBadge>
              </div>
            </div>
            {!sessionId && (
              <GlyphButton
                variant="ghost"
                size="sm"
                onClick={handleChangeAgent}
                icon={<Users size={14} />}
              >
                Cambiar
              </GlyphButton>
            )}
          </div>
        )}

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && !currentAssistant && (
            <p className="text-[var(--bw-muted)] text-sm text-center py-8">
              {activeAgent
                ? `Invocando a ${activeAgent.displayName}. Envía un mensaje.`
                : "Envía un mensaje para chatear con Claude."}
            </p>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex flex-col ${msg.role === "user" ? "items-end" : "items-start"}`}
            >
              <div
                className={`
                  relative group max-w-[85%] px-4 py-2 text-sm break-words
                  ${msg.role === "user"
                    ? "bg-[var(--bw-accent)]/10 border border-[var(--bw-accent)]/30 text-[var(--bw-fg)] whitespace-pre-wrap font-mono"
                    : "bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)]"
                  }
                `}
              >
                {/* Attachment chips inside the bubble */}
                {msg.attachments && msg.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {msg.attachments.map((att, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-1 border border-[var(--bw-border)] bg-[var(--bw-bg)] px-2 py-0.5 text-[10px] font-mono"
                      >
                        {att.kind === "image" ? (
                          <>
                            <Image size={10} className="text-[var(--bw-accent)] flex-shrink-0" />
                            {att.url && (
                              <img src={att.url} alt={att.name} className="h-10 w-10 object-cover" />
                            )}
                          </>
                        ) : (
                          <FileText size={10} className="text-[var(--bw-accent)] flex-shrink-0" />
                        )}
                        <span className="text-[var(--bw-muted)] max-w-[150px] truncate">{att.name}</span>
                      </div>
                    ))}
                  </div>
                )}

                {msg.role === "assistant" ? (
                  <MarkdownMessage content={msg.content} />
                ) : (
                  msg.content
                )}

                {/* Fork button for assistant messages */}
                {msg.role === "assistant" && msg.id && sessionId && (
                  <button
                    onClick={() => handleFork(msg.id!)}
                    className="
                      absolute -right-2 -top-2
                      opacity-0 group-hover:opacity-100
                      p-1 bg-[var(--bw-surface)] border border-[var(--bw-border)]
                      text-[var(--bw-muted)] hover:text-[var(--bw-accent)]
                      transition-all
                    "
                    title="Fork desde aquí"
                  >
                    <GitBranch size={12} />
                  </button>
                )}
              </div>
              {msg.createdAt && (
                <span className="text-[10px] text-[var(--bw-muted)] mt-0.5 px-1 font-mono">
                  {formatTime(msg.createdAt)}
                </span>
              )}
            </div>
          ))}

          {/* Thinking indicator — visible before first token arrives */}
          {loading && !currentAssistant && (
            <div className="flex justify-start items-center gap-2">
              <div className="px-4 py-2 text-sm bg-[var(--bw-surface)] border border-[var(--bw-border)] font-mono">
                <span className="text-[var(--bw-accent)] animate-pulse">
                  {(THEME_PHRASES[themeId] ?? DEFAULT_PHRASES)[phraseIdx]}
                </span>
              </div>
              {sessionId && (
                <button
                  title="Detener tarea"
                  onClick={() => {
                    stopSession.mutate(sessionId);
                    streamAbortRef.current?.abort();
                  }}
                  className="p-1.5 border border-[var(--bw-accent)]/40 text-[var(--bw-accent)] hover:bg-[var(--bw-accent)]/10 transition-colors"
                >
                  <Square size={12} />
                </button>
              )}
            </div>
          )}

          {/* Tool activity — live tool_use lines for the current turn */}
          {toolActivity.length > 0 && (
            <div className="flex justify-start">
              <div className="max-w-[85%] font-mono text-xs space-y-0.5">
                {toolActivity.map((t, i) => (
                  <div
                    key={`${t.toolUseId}-${i}`}
                    className="flex items-center gap-1.5 text-[var(--bw-muted)]"
                  >
                    <span className="text-[var(--bw-accent)]">🔧</span>
                    <span className="truncate">{t.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Streaming response */}
          {currentAssistant && (
            <div className="flex justify-start items-start gap-2">
              <div className="max-w-[85%] px-4 py-2 text-sm bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)]">
                <MarkdownMessage content={currentAssistant} />
                <span className="animate-pulse text-[var(--bw-accent)]">▌</span>
              </div>
              {sessionId && (
                <button
                  title="Detener tarea"
                  onClick={() => {
                    stopSession.mutate(sessionId);
                    streamAbortRef.current?.abort();
                  }}
                  className="mt-2 p-1.5 border border-[var(--bw-accent)]/40 text-[var(--bw-accent)] hover:bg-[var(--bw-accent)]/10 transition-colors flex-shrink-0"
                >
                  <Square size={12} />
                </button>
              )}
            </div>
          )}

          {/* Edits bubble — compact chip, expands on tap */}
          {sessionId && edits.length > 0 && (
            <div className="flex justify-start">
              <div className="font-mono text-xs max-w-[85%]">
                <button
                  type="button"
                  onClick={() => setEditsExpanded((v) => !v)}
                  className="flex items-center gap-2 px-3 py-1.5 border border-[var(--bw-border)] bg-[var(--bw-surface)] hover:border-[var(--bw-accent)] transition-colors"
                >
                  <span className="text-[var(--bw-accent)] text-[10px]">
                    {editsExpanded ? "▾" : "▸"}
                  </span>
                  <span className="uppercase tracking-[0.15em] text-[9px] text-[var(--bw-muted)]">
                    edits
                  </span>
                  <span className="text-[var(--bw-fg)]">
                    {edits.length} archivo{edits.length === 1 ? "" : "s"}
                  </span>
                  <span className="text-[var(--bw-ok,#7fe)]">
                    +{edits.reduce((s, e) => s + e.additions, 0)}
                  </span>
                  <span className="text-[var(--bw-warn,#f77)]">
                    -{edits.reduce((s, e) => s + e.deletions, 0)}
                  </span>
                  {edits.some((e) => e.reverted) && (
                    <span className="text-[var(--bw-muted)] text-[9px]">
                      · {edits.filter((e) => e.reverted).length} rev
                    </span>
                  )}
                </button>

                {editsExpanded && (
                  <div className="border border-t-0 border-[var(--bw-border)] max-h-[420px] overflow-y-auto">
                    {edits.map((e) => (
                      <DiffCard
                        key={e.id}
                        sessionId={sessionId}
                        edit={e}
                        onReverted={(editId) =>
                          setEdits((prev) =>
                            prev.map((x) =>
                              x.id === editId ? { ...x, reverted: true } : x,
                            ),
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] px-4 py-2 text-[var(--bw-danger)] text-sm">
              ⚠ {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Budget bar — per-turn cap (see budgetPercent) */}
        {activeAgent?.maxBudgetUsd && lastUsage?.totalCostUsd !== undefined && (
          <div className="mx-4 mb-2">
            <div className="flex items-center gap-2 text-xs text-[var(--bw-muted)]">
              <span>Budget/turno:</span>
              <div className="flex-1 h-2 bg-[var(--bw-surface)] border border-[var(--bw-border)]">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${budgetPercent ?? 0}%`,
                    backgroundColor:
                      budgetPercent && budgetPercent > 80
                        ? "var(--bw-danger)"
                        : budgetPercent && budgetPercent > 50
                        ? "var(--bw-warn)"
                        : activeAgent.color,
                  }}
                />
              </div>
              <span>
                ${lastUsage.totalCostUsd.toFixed(3)} / ${activeAgent.maxBudgetUsd.toFixed(2)}
                {sessionUsage && (
                  <span className="ml-2 opacity-60">
                    · sesión ${sessionUsage.totalCostUsd.toFixed(2)}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* No cap — still show what the turn cost, so spend never goes dark. */}
        {!activeAgent?.maxBudgetUsd && lastUsage?.totalCostUsd !== undefined && (
          <div className="mx-4 mb-2">
            <div className="flex items-center gap-2 text-xs text-[var(--bw-muted)]">
              <span>Budget/turno:</span>
              <span>
                ${lastUsage.totalCostUsd.toFixed(3)}
                <span className="ml-2 opacity-60">sin tope</span>
                {sessionUsage && (
                  <span className="ml-2 opacity-60">
                    · sesión ${sessionUsage.totalCostUsd.toFixed(2)}
                  </span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Usage info */}
        {lastUsage && (
          <div className="px-4 py-1 text-xs text-[var(--bw-muted)] border-t border-[var(--bw-border)]">
            tokens: {lastUsage.inputTokens ?? "?"} in / {lastUsage.outputTokens ?? "?"} out
            {lastUsage.totalCostUsd !== undefined && (
              <span className="ml-2">(${lastUsage.totalCostUsd.toFixed(4)})</span>
            )}
          </div>
        )}

        {/* Permission request banner — anchored above input, stacks on mobile */}
        {permissionRequest && (
          <div className="mx-4 mb-2">
            <CorpoCard className="bg-[var(--bw-warn)]/10 border-[var(--bw-warn)]">
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-[var(--bw-warn)] flex-shrink-0 mt-0.5" size={16} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[var(--bw-fg)]">
                      Permiso — <span className="text-[var(--bw-warn)]">{permissionRequest.tool}</span>
                    </p>
                    {permissionRequest.input && typeof permissionRequest.input.command === "string" && (
                      <code className="block text-[11px] text-[var(--bw-accent)] mt-1 break-all">
                        {permissionRequest.input.command}
                      </code>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  <GlyphButton size="sm" variant="primary" icon={<Check size={12} />}
                    onClick={async () => {
                      if (!sessionId) return;
                      await fetch(`/api/chat/sessions/${sessionId}/permission`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ allow: true }),
                      });
                      setPermissionRequest(null);
                    }}>
                    Conceder
                  </GlyphButton>
                  <GlyphButton size="sm" variant="ghost" icon={<X size={12} />}
                    onClick={async () => {
                      if (sessionId) await fetch(`/api/chat/sessions/${sessionId}/permission`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ allow: false }),
                      });
                      setPermissionRequest(null);
                    }}>
                    Denegar
                  </GlyphButton>
                  {permissionRequest.tool === "Bash" && permissionRequest.input &&
                    typeof permissionRequest.input.command === "string" && (
                      <GlyphButton size="sm" variant="ghost"
                        onClick={async () => {
                          if (!sessionId || !permissionRequest.input) return;
                          const cmd = permissionRequest.input.command as string;
                          await fetch(`/api/chat/sessions/${sessionId}/allow-command`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ command: cmd }),
                          });
                          await fetch(`/api/chat/sessions/${sessionId}/permission`, {
                            method: "POST", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ allow: true }),
                          });
                          setPermissionRequest(null);
                        }}>
                        + Allow list
                      </GlyphButton>
                    )}
                </div>
              </div>
            </CorpoCard>
          </div>
        )}

        {/* Harness blocked banner — anchored above input */}
        {harnessBlocked && (
          <div className="mx-4 mb-2">
            <CorpoCard className="bg-[var(--bw-danger)]/10 border-[var(--bw-danger)]">
              <div className="flex flex-col gap-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="text-[var(--bw-danger)] flex-shrink-0 mt-0.5" size={16} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-[var(--bw-fg)]">Bloqueado</p>
                    <code className="block text-[11px] text-[var(--bw-accent)] mt-1 break-all">
                      {harnessBlocked.command}
                    </code>
                  </div>
                </div>
                <div className="flex gap-1">
                  <GlyphButton size="sm" variant="primary" icon={<Check size={12} />}
                    onClick={async () => {
                      if (!sessionId) return;
                      const cmd = harnessBlocked.command;
                      await fetch(`/api/chat/sessions/${sessionId}/allow-command`, {
                        method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ command: cmd }),
                      });
                      setHarnessBlocked(null);
                      handleSend(`El comando fue aprobado y añadido al allow-list. Por favor ejecuta de nuevo: ${cmd}`);
                    }}>
                    Agregar
                  </GlyphButton>
                  <GlyphButton size="sm" variant="ghost" icon={<X size={12} />} onClick={() => setHarnessBlocked(null)}>
                    Ignorar
                  </GlyphButton>
                </div>
              </div>
            </CorpoCard>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-[var(--bw-border)] p-4 flex flex-col gap-2">
          {/* Attachment previews */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((att, idx) => (
                <div
                  key={idx}
                  className="relative flex items-center gap-1 border border-[var(--bw-border)] bg-[var(--bw-surface-2)] px-2 py-1 text-xs font-mono"
                >
                  {att.kind === "image" ? (
                    <img src={att.url} alt={att.name} className="h-8 w-8 object-cover" />
                  ) : (
                    <FileText size={14} className="text-[var(--bw-accent)]" />
                  )}
                  <span className="text-[var(--bw-muted)] max-w-[120px] truncate">{att.name}</span>
                  <button
                    onClick={() => removeAttachment(idx)}
                    className="text-[var(--bw-muted)] hover:text-[var(--bw-danger)] ml-1"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            {/* Hidden file input — not available for guests */}
            {!isGuest && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,text/*,.ts,.tsx,.js,.jsx,.py,.rs,.go,.md,.json,.yaml,.yml,.toml,.sh,.sql"
                  className="hidden"
                  onChange={handleFilePick}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={loading}
                  className="
                    flex-shrink-0 p-2 border border-[var(--bw-border)]
                    text-[var(--bw-muted)] hover:text-[var(--bw-accent)]
                    hover:border-[var(--bw-accent)] transition-colors
                    disabled:opacity-40
                  "
                  title="Adjuntar archivo o imagen"
                >
                  <Paperclip size={16} />
                </button>
              </>
            )}

            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder={
                activeAgent
                  ? `Mensaje para ${activeAgent.displayName}…`
                  : "Escribe un mensaje…"
              }
              disabled={loading}
              rows={1}
              className="
                flex-1 min-w-0 bg-[var(--bw-surface)] border border-[var(--bw-border)]
                px-3 py-2 text-sm text-[var(--bw-fg)]
                placeholder-[var(--bw-muted)]
                focus:border-[var(--bw-accent)] focus:outline-none
                resize-none disabled:opacity-50
                font-mono
              "
            />
            <GlyphButton
              onClick={() => handleSend()}
              disabled={loading || (!input.trim() && attachments.length === 0)}
            >
              {loading ? "…" : "Enviar"}
            </GlyphButton>
          </div>
        </div>
      </div>
    </div>

    <NewSessionModal
      open={newSessionModalOpen}
      onClose={() => setNewSessionModalOpen(false)}
      onConfirm={handleNewSessionConfirm}
    />
    </>
  );
}
